/**
 * MusicLibrary — Admin section to upload, preview, and delete background
 * music tracks for the Reels generator.
 *
 * Tracks are stored in MongoDB (survives redeploys) and keyed by
 * "topic" (matches the Daily-Post topic taxonomy + a "default" fallback).
 *
 * UI: a compact card matching DailyStudio's visual language. List of
 * uploaded tracks on the left, upload form on the right.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MusicNotes, Upload, Trash, Play, FileAudio, CircleNotch,
} from "@phosphor-icons/react";
import { http } from "@/lib/api";

function bytesToHuman(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

const TOPIC_LABELS = {
  default: "Standard (alle Themen)",
  bienen: "Bienen & Bestäuber",
  acker: "Acker & Boden",
  weide: "Weide & Grünland",
  wald: "Wald & Forst",
  klima: "Klima & Wetter",
  markt: "Märkte",
  mikroben: "Mikroben & Bodenleben",
  saatgut: "Saatgut & Sorten",
  wasser: "Wasser",
  wissenschaft: "Wissenschaft",
  vision: "Vision & Politik",
};

export default function MusicLibrary() {
  const [uploads, setUploads] = useState([]);
  const [topics, setTopics] = useState([]);
  const [limit, setLimit] = useState(6 * 1024 * 1024);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState("default");
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const [topicsRes, listRes] = await Promise.all([
        http.get(`/admin/daily/music/topics`),
        http.get(`/admin/daily/music`),
      ]);
      setTopics(topicsRes.data?.topics || []);
      setLimit(topicsRes.data?.limit_bytes || 6 * 1024 * 1024);
      setUploads(listRes.data?.uploads || []);
    } catch (err) {
      if (err?.response?.status !== 403) {
        toast.error("Konnte Musik-Bibliothek nicht laden");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Bitte eine Audio-Datei auswählen."); return; }
    if (file.size > limit) {
      toast.error(`Datei zu groß (${bytesToHuman(file.size)}, max. ${bytesToHuman(limit)}).`);
      return;
    }
    const form = new FormData();
    form.append("file", file);
    form.append("topic", selectedTopic);
    if (label.trim()) form.append("label", label.trim());
    setUploading(true);
    try {
      // http (axios instance) attaches the Bearer token via interceptor.
      // Override Content-Type from the instance default so FormData works.
      await http.post(`/admin/daily/music`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Track für „${TOPIC_LABELS[selectedTopic] || selectedTopic}" hochgeladen.`);
      setLabel("");
      if (fileRef.current) fileRef.current.value = "";
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (topic) => {
    if (!window.confirm(`„${TOPIC_LABELS[topic] || topic}"-Track wirklich löschen?`)) return;
    try {
      await http.delete(`/admin/daily/music/${topic}`);
      toast.success("Gelöscht");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte nicht löschen.");
    }
  };

  return (
    <div
      className="eid-card p-4"
      style={{ borderColor: "rgba(255,255,255,0.10)" }}
      data-testid="music-library-card"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-sm font-medium text-white flex items-center gap-1.5">
          <MusicNotes size={14} weight="fill" className="text-[#D9A87C]" />
          Reels-Hintergrundmusik
        </div>
        <div className="text-[11px] font-mono text-zinc-500">
          {uploads.length}/{topics.length || "·"} Topics · max. {bytesToHuman(limit)}/Datei
        </div>
      </div>

      <div className="grid md:grid-cols-[1.4fr_1fr] gap-4">
        {/* Existing tracks */}
        <div className="space-y-2" data-testid="music-library-list">
          {loading ? (
            <div className="text-xs text-zinc-500 py-4">Lade…</div>
          ) : uploads.length === 0 ? (
            <div className="text-xs text-zinc-500 py-4 border border-dashed border-white/10 rounded-sm px-3 text-center">
              Noch keine eigenen Tracks — Bodenblick benutzt die prozeduralen Ambient-Pads.
            </div>
          ) : (
            uploads.map((u) => (
              <div
                key={u.topic}
                className="flex items-center gap-2 border border-white/10 rounded-sm px-3 py-2 bg-[#121212]"
                data-testid={`music-row-${u.topic}`}
              >
                <FileAudio size={16} className="text-[#D9A87C]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">
                    {u.label || u.filename}
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500 truncate">
                    {TOPIC_LABELS[u.topic] || u.topic} · {bytesToHuman(u.size_bytes)} · {(u.uploaded_at || "").slice(0, 10)}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await http.get(
                        `/admin/daily/music/${u.topic}/preview`,
                        { responseType: "blob" }
                      );
                      const url = URL.createObjectURL(res.data);
                      const audio = new Audio(url);
                      audio.play().catch(() => toast.error("Kann nicht abspielen."));
                      audio.addEventListener("ended", () => URL.revokeObjectURL(url));
                    } catch {
                      toast.error("Vorschau fehlgeschlagen.");
                    }
                  }}
                  className="p-1.5 text-zinc-400 hover:text-white"
                  title="Vorschau anhören"
                  data-testid={`music-play-${u.topic}`}
                >
                  <Play size={14} weight="fill" />
                </button>
                <button
                  onClick={() => onDelete(u.topic)}
                  className="p-1.5 text-[#FF7A45] hover:text-[#FF7A45]"
                  title="Löschen"
                  data-testid={`music-delete-${u.topic}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Upload form */}
        <form onSubmit={onUpload} className="space-y-2" data-testid="music-upload-form">
          <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-400">
            Topic
          </label>
          <select
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value)}
            className="w-full text-sm bg-[#121212] border border-white/10 rounded-sm px-2.5 py-1.5 outline-none focus:border-[#D9A87C]/40"
            data-testid="music-topic-select"
          >
            {topics.map((t) => (
              <option key={t} value={t}>{TOPIC_LABELS[t] || t}</option>
            ))}
          </select>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-1">
            Bezeichnung (optional)
          </label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="z. B. Bienensummen Frühling 2026"
            maxLength={80}
            className="w-full text-sm bg-[#121212] border border-white/10 rounded-sm px-2.5 py-1.5 outline-none focus:border-[#D9A87C]/40"
            data-testid="music-label-input"
          />
          <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-1">
            Audio (MP3 / M4A / OGG / WAV, max. {bytesToHuman(limit)})
          </label>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.mp3,.m4a,.aac,.wav,.ogg"
            className="w-full text-xs text-zinc-400"
            data-testid="music-file-input"
          />
          <button
            type="submit"
            disabled={uploading}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#7A4A2D] hover:bg-[#5C3722] text-white rounded-sm text-sm disabled:opacity-50"
            data-testid="music-upload-btn"
          >
            {uploading ? <CircleNotch size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Lade hoch…" : "Hochladen"}
          </button>
          <p className="text-[10px] text-zinc-500 leading-snug pt-1">
            Bitte nur lizenzfreie oder selbst-komponierte Musik. Tracks werden
            in MongoDB gespeichert und überleben Deployments. Re-Upload für
            dasselbe Topic überschreibt die vorige Datei.
          </p>
        </form>
      </div>
    </div>
  );
}
